/**
 * Dialog criar/editar de template de checklist — sempre cross-mesa: o select
 * de mesa fica no próprio form. POST cria; PUT substitui os itens pela lista
 * enviada e, ao trocar deskId, o servidor reposiciona o template na mesa
 * destino.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import type { ChecklistTemplate, ChecklistTemplateBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog } from '@/components/ui/misc';
import { desksListOptions, errorMessage } from '@/features/desks/desks-queries';

export function ChecklistTemplateDialog({
  template,
  onClose,
}: {
  template: ChecklistTemplate | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // ── Mesa: obrigatória; muda também na edição (o PUT leva deskId) ──────────
  const [selectedDeskId, setSelectedDeskId] = useState<number | null>(template?.deskId ?? null);
  const desksList = useQuery(desksListOptions());
  const [name, setName] = useState(template?.name ?? '');
  const [active, setActive] = useState(template?.active ?? true);
  const [items, setItems] = useState<string[]>(
    template ? [...template.items].sort((a, b) => a.position - b.position).map((i) => i.text) : []
  );
  const [newItem, setNewItem] = useState('');

  const save = useMutation({
    mutationFn: async (body: ChecklistTemplateBody) => {
      const res = template
        ? await api<ChecklistTemplate>(`/checklists/${template.id}`, { method: 'PUT', body })
        : await api<ChecklistTemplate>('/checklists', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // prefixo largo: cobre a lista cross-mesa e as listas por mesa
      void queryClient.invalidateQueries({ queryKey: ['erp', 'checklists'] });
      onClose();
    },
  });

  const addItem = () => {
    const text = newItem.trim();
    if (!text) return;
    setItems((prev) => [...prev, text]);
    setNewItem('');
  };

  const move = (index: number, delta: -1 | 1) => {
    setItems((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      if (moved === undefined) return prev;
      next.splice(target, 0, moved);
      return next;
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selectedDeskId === null) return;
    save.mutate({ deskId: selectedDeskId, name: name.trim(), active, items });
  };

  return (
    <Dialog open onClose={onClose} title={template ? 'Editar checklist' : 'Novo checklist'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="checklist-desk">
            Mesa <span className="text-destructive">*</span>
          </Label>
          <Select
            id="checklist-desk"
            value={selectedDeskId === null ? '' : String(selectedDeskId)}
            onChange={(e) => setSelectedDeskId(e.target.value === '' ? null : Number(e.target.value))}
            required
          >
            <option value="" disabled>
              Selecione a mesa
            </option>
            {[...(desksList.data?.items ?? [])]
              .sort((a, b) => a.position - b.position)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.displayName}
                </option>
              ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="checklist-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="checklist-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch checked={active} onCheckedChange={setActive} />
          Checklist ativo
        </label>

        <div className="space-y-1.5">
          <Label>Itens</Label>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum item ainda.</p>
          ) : (
            <ul className="space-y-1">
              {items.map((text, i) => (
                <li
                  key={`${i}-${text}`}
                  className="flex items-center gap-1.5 rounded-md border border-divider px-2 py-1.5 text-sm"
                >
                  <span className="min-w-0 flex-1 truncate">{text}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground"
                    title="Mover para cima"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowUp className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground"
                    title="Mover para baixo"
                    disabled={i === items.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDown className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground hover:text-destructive"
                    title="Remover item"
                    onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <X className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Novo item…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addItem();
                }
              }}
            />
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={addItem}>
              <Plus className="size-4" />
              Adicionar
            </Button>
          </div>
        </div>

        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={save.isPending || !name.trim() || selectedDeskId === null}
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
